import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Send, FileDown, FileSpreadsheet, Pencil, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { TaskItem } from "@/components/TaskItem";
import { FollowUpTable } from "@/components/FollowUpTable";
import { api, completion, exportUrl, errMsg } from "@/lib/api";

const Section = ({ title, children, testid }) => (
  <section className="bg-white border border-[#E2DAD0] rounded-2xl p-5 sm:p-6 rise" data-testid={testid}>
    <h2 className="text-base md:text-lg font-semibold text-[#2A3C36] mb-4">{title}</h2>
    {children}
  </section>
);

export default function ChecklistPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cl, setCl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/checklists/${id}`).then((r) => setCl(r.data)).catch(() => { toast.error("Checklist not found"); nav("/"); });
  }, [id, nav]);

  if (!cl) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C87D55]" /></div>;

  const locked = cl.status === "submitted";
  const pct = completion(cl.items);
  const done = cl.items.filter((i) => i.done).length;
  const set = (patch) => setCl({ ...cl, ...patch });

  const save = async (status) => {
    if (status === "submitted" && !cl.signature.trim()) return toast.error("Please type your name as signature before submitting");
    setSaving(true);
    try {
      const { data } = await api.put(`/checklists/${id}`, { ...cl, status });
      setCl(data);
      toast.success(status === "submitted" ? "Shift report submitted" : "Progress saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-28">
      <div className="rise flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C87D55]">{cl.date}</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2A3C36] mt-1" data-testid="checklist-title">{cl.shift} Shift</h1>
          <p className="text-sm text-[#666059] mt-1" data-testid="checklist-staff">FDA: <b>{cl.staff_name}</b></p>
        </div>
        <span data-testid="checklist-status" className={`self-start sm:self-auto px-3 py-1 rounded-full text-xs font-medium ${locked ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEF3C7] text-[#B45309]"}`}>
          {locked ? "Submitted" : "Draft"}
        </span>
      </div>

      <div className="bg-[#2A3C36] text-white rounded-2xl p-5 sm:p-6 rise">
        <div className="flex justify-between items-baseline mb-3">
          <span className="text-sm text-white/70">Progress</span>
          <span className="font-heading text-2xl font-semibold" data-testid="checklist-progress">{done}/{cl.items.length} · {pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/15 overflow-hidden">
          <div className="h-full bg-[#C87D55] transition-[width] duration-500 ease-out" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <Section title="Daily Tasks" testid="tasks-section">
        {cl.items.length === 0 ? (
          <p className="text-sm text-[#9A9188]">No tasks set for this shift yet. Add them in the Tasks page.</p>
        ) : (
          <div className="-mx-5 sm:-mx-6 divide-y divide-[#F0EBE3] border-y border-[#F0EBE3]">
            {cl.items.map((it, i) => (
              <TaskItem key={i} index={i} item={it} disabled={locked} onChange={(n) => set({ items: cl.items.map((x, j) => (j === i ? n : x)) })} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Daily Notes / Follow-up" testid="followup-section">
        <FollowUpTable rows={cl.follow_ups} disabled={locked} onChange={(rows) => set({ follow_ups: rows })} />
      </Section>

      <Section title="Shift Handover Notes" testid="handover-section">
        <Textarea data-testid="handover-notes-input" disabled={locked} value={cl.handover_notes} onChange={(e) => set({ handover_notes: e.target.value })} placeholder="Important info for the next shift..." className="min-h-[120px] bg-[#FBF9F5]" />
        <div className="mt-5 max-w-sm space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-[#666059]">FDA Signature (type your name)</label>
          <Input data-testid="signature-input" disabled={locked} value={cl.signature} onChange={(e) => set({ signature: e.target.value })} placeholder={cl.staff_name} className="h-11 bg-[#FBF9F5] font-accent italic text-lg" />
        </div>
      </Section>

      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur-md border-t border-[#E2DAD0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" className="rounded-full" data-testid="checklist-pdf-button" onClick={() => window.open(exportUrl("pdf", { id }), "_blank")}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
          <Button variant="outline" className="rounded-full" data-testid="checklist-excel-button" onClick={() => window.open(exportUrl("excel", { id }), "_blank")}><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
          {locked ? (
            <Button className="rounded-full bg-[#2A3C36] hover:bg-[#1f2d28]" data-testid="checklist-reopen-button" disabled={saving} onClick={() => save("draft")}><Pencil className="w-4 h-4 mr-1" />Edit again</Button>
          ) : (
            <>
              <Button variant="outline" className="rounded-full" data-testid="checklist-save-button" disabled={saving} onClick={() => save("draft")}><Save className="w-4 h-4 mr-1" />Save</Button>
              <Button className="rounded-full bg-[#C87D55] hover:bg-[#A85832]" data-testid="checklist-submit-button" disabled={saving} onClick={() => save("submitted")}><Send className="w-4 h-4 mr-1" />Submit</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
